// ============================================================================
// MODAL - sensorSimulation.jsx (Enhanced)
// ============================================================================

import React, { useState, useEffect } from 'react';
import useComponentLibrary from '../../hooks/useComponentLibrary';
import './sensorSimulation.css';

const SensorSimulation = ({ 
  visible, 
  sensor, 
  onClose, 
  onSave 
}) => {
  const [generationType, setGenerationType] = useState('manual');
  const [baseValues, setBaseValues] = useState({});
  const [variation, setVariation] = useState({});
  const [updateFrequency, setUpdateFrequency] = useState(2000);
  const [customFunction, setCustomFunction] = useState('');
  const [manualValue, setManualValue] = useState({});
  const [outputKeys, setOutputKeys] = useState([]); 
  const [outputRanges, setOutputRanges] = useState({});

  const { getComponentById } = useComponentLibrary();

  useEffect(() => {
    
    const loadSensorOutputs = async () => {
      if (!sensor) return;
      
      let keys = [];
      let ranges = {};
      
      // If sensor already has current values, extract keys from there
      if (sensor.currentValue) {
        keys = Object.keys(sensor.currentValue);
      }
      
      // Always try to fetch component data to get accurate ranges
      try {
        const response = await getComponentById(sensor.componentId);
        if (response?.success && response.data?.simulation?.outputs) {
          keys = response.data.simulation.outputs.map(output => output.name);
          
          // Extract ranges for each output
          response.data.simulation.outputs.forEach(output => {
            ranges[output.name] = {
              min: output.range.min,
              max: output.range.max,
              resolution: output.resolution || 0.1
            };
          });
        }
      } catch (err) {
        console.error('Error fetching component:', err);
        // If fetch fails and we have no keys, we can't proceed
        if (keys.length === 0) {
          console.error('Cannot determine sensor outputs');
          return;
        }
      }
      
      setOutputKeys(keys);
      setOutputRanges(ranges);
      
      // if (sensor.simulationConfig) {
      //   const config = sensor.simulationConfig;
      //   setGenerationType(config.generationType || 'random_range');
      //   setBaseValues(config.baseValues || {});
      //   setVariation(config.variation || {});
      //   setUpdateFrequency(config.updateFrequency || 2000);
      //   setCustomFunction(config.customFunction || '');
      //   setManualValue(config.manualValue || {});
      // } 
      if (sensor.simulationConfig) {
        const config = sensor.simulationConfig;
        setGenerationType(config.generationType || 'random_range');
        
        // ✅ FIX: Validate and correct baseValues against actual sensor ranges
        const correctedBaseValues = {};
        keys.forEach(key => {
          const range = ranges[key];
          const existingBase = config.baseValues?.[key];
          
          if (range && existingBase) {
            // Clamp existing values to actual sensor range
            correctedBaseValues[key] = {
              min: Math.max(range.min, Math.min(range.max, existingBase.min)),
              max: Math.max(range.min, Math.min(range.max, existingBase.max))
            };
          } else if (range) {
            // Use actual range if no existing value
            correctedBaseValues[key] = { min: range.min, max: range.max };
          } else {
            // Fallback
            correctedBaseValues[key] = existingBase || { min: 0, max: 100 };
          }
        });
        
        setBaseValues(correctedBaseValues);
        setVariation(config.variation || {});
        setUpdateFrequency(config.updateFrequency || 2000);
        setCustomFunction(config.customFunction || '');
        setManualValue(config.manualValue || {});
      } else {
        const initialBase = {};
        const initialVar = {};
        const initialManual = {};
        
        keys.forEach(key => {
          const range = ranges[key];
          // Use actual sensor range from component library
          initialBase[key] = { 
            min: range?.min || 0, 
            max: range?.max || 100 
          };
          initialVar[key] = 5;
          // Initialize manual value to midpoint of range
          initialManual[key] = range ? (range.min + range.max) / 2 : 0;
        });
        
        setBaseValues(initialBase);
        setVariation(initialVar);
        setManualValue(initialManual);
      }
    };
    loadSensorOutputs();
  }, [sensor]);

  if (!visible || !sensor) return null;

  const handleSave = () => {
    const config = {
      generationType,
      baseValues,
      variation,
      updateFrequency,
      customFunction,
      manualValue
    };
    
    onSave(sensor.id, config);
    onClose();
  };

  const generationTypes = [
    { value: 'manual', icon: '✏️', label: 'Manual' },
    { value: 'random_range', icon: '🎲', label: 'Random Range' },
    { value: 'sine_wave', icon: '〰️', label: 'Sine Wave' },
    { value: 'step_function', icon: '📊', label: 'Step Function' },
    { value: 'custom', icon: '⚡', label: 'Custom' }
  ];

  const renderOutputField = (key) => (
    <div key={key} className="output-field">
      <div className="output-label">{key}</div>
      <div className="output-controls">
        {generationType === 'manual' ? (
          <input
            type="number"
            value={manualValue[key] ?? ''}
            min={outputRanges[key]?.min}
            max={outputRanges[key]?.max}
            step={outputRanges[key]?.resolution || 0.1}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              const range = outputRanges[key];
              const clampedValue = range ? Math.max(range.min, Math.min(range.max, value)) : value;
              setManualValue({
                ...manualValue,
                [key]: clampedValue
              });
            }}
            className="value-input"
            placeholder="Value"
          />
        ) : generationType === 'step_function' ? (
          <>
            <input
              type="number"
              placeholder="Low"
              value={baseValues[key]?.min ?? ''}
              min={outputRanges[key]?.min}
              max={outputRanges[key]?.max}
              // onChange={(e) => {
              //   const value = parseFloat(e.target.value);
              //   const range = outputRanges[key];
              //   const clampedValue = range ? Math.max(range.min, Math.min(range.max, value)) : value;
              //   setBaseValues({
              //     ...baseValues,
              //     [key]: { ...baseValues[key], min: clampedValue }
              //   });
              // }}
              onChange={(e) => setBaseValues({
                ...baseValues,
                [key]: { ...baseValues[key], min: e.target.value }
              })}
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                const range = outputRanges[key];
                const clamped = range ? Math.max(range.min, Math.min(range.max, value)) : value;
                setBaseValues({
                  ...baseValues,
                  [key]: { ...baseValues[key], min: isNaN(clamped) ? range?.min ?? 0 : clamped }
                });
              }}
              className="value-input"
            />
            <input
              type="number"
              placeholder="High"
              value={baseValues[key]?.max ?? ''}
              min={outputRanges[key]?.min}
              max={outputRanges[key]?.max}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                const range = outputRanges[key];
                const clampedValue = range ? Math.max(range.min, Math.min(range.max, value)) : value;
                setBaseValues({
                  ...baseValues,
                  [key]: { ...baseValues[key], max: clampedValue }
                });
              }}
              className="value-input"
            />
          </>
        ) : (
          <>
            <input
              type="number"
              placeholder="Min"
              value={baseValues[key]?.min ?? ''}
              min={outputRanges[key]?.min}
              max={outputRanges[key]?.max}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                const range = outputRanges[key];
                const clampedValue = range ? Math.max(range.min, Math.min(range.max, value)) : value;
                setBaseValues({
                  ...baseValues,
                  [key]: { ...baseValues[key], min: clampedValue }
                });
              }}
              className="value-input"
            />
            <input
              type="number"
              placeholder="Max"
              value={baseValues[key]?.max ?? ''}
              min={outputRanges[key]?.min}
              max={outputRanges[key]?.max}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                const range = outputRanges[key];
                const clampedValue = range ? Math.max(range.min, Math.min(range.max, value)) : value;
                setBaseValues({
                  ...baseValues,
                  [key]: { ...baseValues[key], max: clampedValue }
                });
              }}
              className="value-input"
            />
            <div className="variation-control">
              <span className="variation-symbol">±</span>
              <input
                type="number"
                placeholder="Var"
                value={variation[key] ?? ''}
                onChange={(e) => setVariation({
                  ...variation,
                  [key]: parseFloat(e.target.value)
                })}
                className="variation-input"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderConfigContent = () => {
    if (generationType === 'custom') {
      return (
        <div className="custom-function-section">
          <div className="function-template">
            <code>
              {`({ name, range, dataType, timestamp, random }) => {
  return random * 100;
}`}
            </code>
          </div>
          <textarea
            value={customFunction}
            onChange={(e) => setCustomFunction(e.target.value)}
            className="function-input"
            placeholder="Enter your custom function..."
            rows={8}
          />
        </div>
      );
    }

    return (
      <>
        <div className="outputs-grid">
          {outputKeys.map(renderOutputField)}
        </div>
        
        {generationType !== 'manual' && (
          <div className="frequency-control">
            <label>
              {generationType === 'sine_wave' ? 'Cycle Period' : 'Update Interval'}
            </label>
            <div className="frequency-input-wrapper">
              <input
                type="number"
                value={updateFrequency}
                onChange={(e) => setUpdateFrequency(parseInt(e.target.value))}
                className="frequency-input"
              />
              <span className="unit">ms</span>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content-top" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-top">
          <div className="header-content">
            <div className="header-icon">⚙️</div>
            <div className="header-text">
              <h3>Configure Sensor</h3>
              <p>{sensor.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>

        <div className="modal-body">
          <div className="generation-types">
            {generationTypes.map(type => (
              <button
                key={type.value}
                className={`type-btn ${generationType === type.value ? 'active' : ''}`}
                onClick={() => setGenerationType(type.value)}
              >
                <span className="type-icon">{type.icon}</span>
                <span className="type-label">{type.label}</span>
              </button>
            ))}
          </div>

          <div className="config-content">
            {renderConfigContent()}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary">
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default SensorSimulation;